import { Link } from 'react-router-dom';
import { Panel } from '../ui/Panel';
import { useCan } from '../useCan';
import { useAdmin } from '../AdminContext';
import { useCosts } from '../margin/useCosts';
import { boxContribution, orderContribution, productMargin } from '../../core/rules/margin';
import { exVat, vatRateOf } from '../../core/rules/revenue';
import { euro, money, toCents } from '../../core/format/currency';
import type { Product } from '../../core/types/catalog';
import type { CostModel } from '../../core/types/margin';

/**
 * Product › Marge: inkoop, verkoop zonder btw en wat er overblijft als losse bestelling of in een
 * bijvuldoos. Alleen met `finance.margin`; nooit op de site.
 */
export function ProductMarginPanel({
  product
}: {
  product: Product;
}) {
  const {
    canAll
  } = useCan();
  const {
    site
  } = useAdmin();
  const allowed = canAll('finance.margin');
  const {
    data
  } = useCosts(allowed);
  if (!allowed || product.kind === 'giftcard' || product.kind === 'kuur' || product.price <= 0) return null;
  const gross = toCents(product.price);
  const vat = vatRateOf(product);
  const cost = data?.itemCosts[product.id];
  return <Panel title="Marge" description="Zonder btw, met je ingeschatte kosten." data-id="element-747" data-mp="1g02fhm7lji73">
      {!data ? <p className="text-[13px] text-muted" data-id="element-748" data-mp="1l1rtnaq48bze">Laden…</p> : cost === undefined ? <p className="text-[13px] text-muted" data-id="element-749" data-mp="1pyi6zqajcth4">
          Nog geen inkoopprijs.{' '}
          <Link to="/be/voorraad?tab=inkoop" className="text-ink underline underline-offset-4" data-id="element-750" data-mp="scb1me6eqx3p">
            Boek een inkoopfactuur
          </Link>
          .
        </p> : <MarginFacts gross={gross} vat={vat} cost={cost} costs={data.costs} policy={site.policy} data-id="element-751" data-mp="12a8rt77tv3yy" />}
    </Panel>;
}
type FactsProps = {
  gross: number;
  vat: number;
  cost: number;
  costs: CostModel;
  policy: {
    freeShippingFrom: number;
    shippingCost: number;
    subscription: {
      minBoxValue: number;
    };
  };
};
function MarginFacts({
  gross,
  vat,
  cost,
  costs,
  policy
}: FactsProps) {
  const m = productMargin(gross, vat, cost);
  const paysShipping = gross / 100 < policy.freeShippingFrom;
  const single = orderContribution({
    kind: 'los',
    lines: [{
      name: 'Dit product',
      qty: 1,
      grossCents: gross,
      vatRate: vat,
      costCents: cost
    }],
    paymentMethod: 'ideal',
    paidCents: gross + (paysShipping ? toCents(policy.shippingCost) : 0),
    shipping: 'pakket-nl',
    ...(paysShipping ? {
      shippingChargedCents: toCents(policy.shippingCost),
      shippingVatRate: 21
    } : {})
  }, costs);
  const alone = gross / 100 >= policy.subscription.minBoxValue;
  const percent = m.percent ?? 0;
  return <dl className="space-y-2 text-[13px]" data-id="element-752" data-mp="3too9cfkmhj66">
      <Row label="Inkoop (gemiddeld)" value={money(cost)} data-id="element-753" data-mp="3tppk0ixq83nd" />
      <Row label="Verkoop zonder btw" value={money(exVat(gross, vat))} data-id="element-754" data-mp="3ooa0wwf4077m" />
      <Row label="Brutomarge" value={`${money(m.marginCents ?? 0)} · ${percent.toLocaleString('nl-NL')}%`} strong data-id="element-755" data-mp="18xxah8kxe0jm" />
      <div className="border-t border-line pt-2" data-id="element-756" data-mp="13zvq8kbrjv6k" />
      <Row label={paysShipping ? 'Losse bestelling (klant betaalt verzending)' : 'Losse bestelling, gratis verzonden'} value={money(single.leftCents)} tone={single.leftCents < 1000 ? 'warn' : undefined} data-id="element-757" data-mp="1416v6ngy38z9" />
      <Row label={alone ? 'Alleen in een bijvuldoos' : `In een bijvuldoos (≥ ${euro(policy.subscription.minBoxValue)})`} value={alone ? money(boxContribution(gross, percent, costs, {
      vatRate: vat
    })) : `+ ${money(m.marginCents ?? 0)} marge`} data-id="element-758" data-mp="1gflqhfiusmce" />
    </dl>;
}
function Row({
  label,
  value,
  strong,
  tone
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'warn';
}) {
  return <div className="flex justify-between gap-4" data-id="element-759" data-mp="1za0n4yhp02x2">
      <dt className="text-muted" data-id="element-760" data-mp="1lfq8xb3bnr59">{label}</dt>
      <dd className={`shrink-0 tabular-nums ${tone === 'warn' ? 'text-clay' : 'text-ink'} ${strong ? 'font-medium' : ''}`} data-id="element-761" data-mp="1leykqlc20vp0">{value}</dd>
    </div>;
}